import React, { useEffect, useState } from "react";
import { LOGO_URL, NAV_LINKS, CONTACT } from "@/lib/brand";
import { Menu, X, Instagram } from "lucide-react";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", open);
    return () => document.body.classList.remove("no-scroll");
  }, [open]);

  const jumpTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className="ga-header"
      style={{
        boxShadow: scrolled ? "0 1px 0 rgba(36,36,34,0.06)" : "none",
      }}
    >
      <div className="ga-container flex items-center justify-between h-16 md:h-20">
        <button
          data-testid="header-logo"
          onClick={() => jumpTo("top")}
          className="flex items-center gap-3"
          aria-label="GA6 Home"
        >
          <img src={LOGO_URL} alt="GA6" className="h-8 md:h-9 w-auto" />
          <span className="hidden md:inline overline">Microcement · India</span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => jumpTo(l.id)}
              className="text-[13px] tracking-[0.18em] uppercase font-medium ga-link text-[color:var(--ga-ink)]"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={CONTACT.instagram}
            target="_blank"
            rel="noreferrer"
            data-testid="header-instagram"
            className="hidden md:inline-flex p-2 hover:opacity-70 transition-opacity"
            aria-label="Instagram"
          >
            <Instagram size={18} strokeWidth={1.25} />
          </a>
          <button
            data-testid="header-cta"
            onClick={() => jumpTo("contact")}
            className="hidden md:inline-flex ga-btn"
            style={{ padding: "0.75rem 1.25rem" }}
          >
            Request Sample
            <span className="arrow">→</span>
          </button>
          <button
            data-testid="header-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} strokeWidth={1.25} /> : <Menu size={22} strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden fixed inset-0 top-16 z-40 bg-[color:var(--ga-bg)]"
        >
          <div className="ga-container py-10 flex flex-col gap-6">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => jumpTo(l.id)}
                className="text-left font-display text-4xl text-[color:var(--ga-ink)]"
              >
                {l.label}
              </button>
            ))}
            <div className="ga-hr my-4" />
            <a href={`tel:${CONTACT.phone}`} className="overline">{CONTACT.phoneDisplay}</a>
            <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="overline">
              {CONTACT.instagramHandle}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
